import {
  hasPendingIn,
  heatAxis,
  heatCoverage,
  postHourMark,
  type HeatSpan,
} from '@reditelji/shared';
import type { OverviewPayload } from '../../api/overview';
import type { WallClock } from '../../live/clock';
import { dayPhase, type DayPhase } from '../../lib/ui';

/**
 * Pokritost po urah (SPEC §1.1A): the Pregled heatmap's model, built from the
 * overview payload. Every figure the card draws — cells, the day bar, row
 * summaries, hotspots, the amber corner, which sectors hide — reads the same
 * view marks, so no two of them can disagree about a post in an hour.
 */

export interface HeatLenses {
  /** "Manjka oseba": a gap, or a rejection as the post's best cover. */
  short: boolean;
  /** "Čaka potrditev". */
  pending: boolean;
}

export interface HeatOptions extends HeatLenses {
  urgentOnly: boolean;
  hideQuiet: boolean;
}

/** A post's mark in an hour, after the lenses. `neutral` is a problem filtered out. */
export type ViewMark = 'short' | 'pending' | 'neutral' | 'ok';

export interface HeatCounts {
  short: number;
  pending: number;
  neutral: number;
  ok: number;
  /** Posts with a window in the hour — the four above add up to it. */
  open: number;
}

export type CellKind = 'empty' | 'short' | 'pending' | 'neutral' | 'ok';

export interface HeatCell extends HeatCounts {
  hour: number;
  kind: CellKind;
  /** Ramp step for red and amber cells: 1 · 2–3 · 4–7 · 8+; 0 otherwise. */
  bin: 0 | 1 | 2 | 3 | 4;
  /** A red cell that also holds posts waiting for a confirmation. */
  corner: boolean;
  past: boolean;
}

export interface HeatPost {
  postId: string;
  code: string;
  micro: string;
  urgent: boolean;
  windows: { fromMin: number; toMin: number }[];
  coverage: HeatSpan[];
  /** View mark per axis hour, `null` where no window reaches. */
  marks: (ViewMark | null)[];
}

export interface HeatSummary {
  /** Posts with a red mark in an hour not yet past. */
  short: number;
  /** Posts with an amber mark in an hour not yet past. */
  pending: number;
}

export interface HeatSector {
  key: string;
  sectorId: string;
  name: string;
  posts: HeatPost[];
  cells: HeatCell[];
  summary: HeatSummary;
}

export interface HeatDay {
  dayId: string;
  label: string;
  date: string;
  index: number;
  phase: DayPhase;
  /** Whole-day stacks per axis hour, for the day bar. */
  hours: HeatCell[];
  /** The hour with the most posts open; the earliest on a tie. */
  peak: { hour: number; open: number } | null;
  summary: HeatSummary;
  /** Every sector with a window that day, for the day-bar popover. */
  allSectors: HeatSector[];
  /** What the band shows once "Skrij sektorje brez težav" has had its say. */
  sectors: HeatSector[];
  /** Why the band shows no sector rows, when it shows none. */
  empty: 'no-windows' | 'no-urgent' | 'all-quiet' | null;
}

export interface Hotspot {
  dayId: string;
  label: string;
  hour: number;
  count: number;
}

export interface HeatModel {
  hours: number[];
  days: HeatDay[];
  /** Worst hour not yet past, per lens that is on. */
  hotspots: { short: Hotspot | null; pending: Hotspot | null };
  /** The tallest day-bar stack, which sets the bar's shared height scale. */
  maxOpen: number;
}

export function isPastHour(phase: DayPhase, hour: number, clock: WallClock): boolean {
  return phase === 'past' || (phase === 'live' && (hour + 1) * 60 <= clock.minutes);
}

export function viewMark(
  mark: 'short' | 'pending' | 'ok' | null,
  coverage: HeatSpan[],
  hour: number,
  lenses: HeatLenses,
): ViewMark | null {
  if (mark === 'short') {
    if (lenses.short) return 'short';
    return lenses.pending && hasPendingIn(coverage, hour) ? 'pending' : 'neutral';
  }
  if (mark === 'pending') return lenses.pending ? 'pending' : 'neutral';
  return mark;
}

export function heatBin(n: number): 0 | 1 | 2 | 3 | 4 {
  if (n <= 0) return 0;
  if (n >= 8) return 4;
  if (n >= 4) return 3;
  if (n >= 2) return 2;
  return 1;
}

function cellOf(posts: HeatPost[], i: number, hour: number, past: boolean): HeatCell {
  const c: HeatCounts = { short: 0, pending: 0, neutral: 0, ok: 0, open: 0 };
  for (const p of posts) {
    const m = p.marks[i];
    if (!m) continue;
    c.open++;
    c[m]++;
  }
  const kind: CellKind = !c.open
    ? 'empty'
    : c.short
      ? 'short'
      : c.pending
        ? 'pending'
        : c.neutral
          ? 'neutral'
          : 'ok';
  const bin = kind === 'short' ? heatBin(c.short) : kind === 'pending' ? heatBin(c.pending) : 0;
  return { ...c, hour, kind, bin, corner: kind === 'short' && c.pending > 0, past };
}

function summaryOf(posts: HeatPost[], pastAt: boolean[]): HeatSummary {
  let short = 0;
  let pending = 0;
  for (const p of posts) {
    const live = p.marks.filter((_, i) => !pastAt[i]);
    if (live.includes('short')) short++;
    if (live.includes('pending')) pending++;
  }
  return { short, pending };
}

const byCode = (a: { code: string }, b: { code: string }) =>
  a.code.localeCompare(b.code, 'sl', { numeric: true });

export function buildHeat(payload: OverviewPayload, opts: HeatOptions, clock: WallClock): HeatModel {
  const allWindows = payload.grid.flatMap((row) => Object.values(row.cells).flatMap((c) => c.windows));
  const axis = heatAxis(allWindows);
  const hours = Array.from({ length: axis.toHour - axis.fromHour }, (_, i) => axis.fromHour + i);

  let maxOpen = 0;
  const days: HeatDay[] = payload.days.map((d, index) => {
    const phase = dayPhase(d.date, clock.isoDate);
    const pastAt = hours.map((h) => isPastHour(phase, h, clock));

    const withWindows = payload.grid.filter((row) => (row.cells[d.id]?.windows.length ?? 0) > 0);
    const visible = opts.urgentOnly ? withWindows.filter((row) => row.urgent) : withWindows;

    const bySector = new Map<string, { name: string; posts: HeatPost[] }>();
    for (const row of visible) {
      const cell = row.cells[d.id]!;
      const coverage = heatCoverage(cell.windows, cell.gaps, cell.shifts);
      const post: HeatPost = {
        postId: row.postId,
        code: row.code,
        micro: row.micro,
        urgent: row.urgent,
        windows: cell.windows,
        coverage,
        marks: hours.map((h) =>
          viewMark(postHourMark(cell.windows, coverage, h), coverage, h, opts),
        ),
      };
      const group = bySector.get(row.sectorId) ?? { name: row.sectorName, posts: [] };
      group.posts.push(post);
      bySector.set(row.sectorId, group);
    }

    const allSectors: HeatSector[] = [...bySector.entries()]
      .map(([sectorId, g]) => {
        const posts = g.posts.sort(byCode);
        return {
          key: `${d.id}:${sectorId}`,
          sectorId,
          name: g.name,
          posts,
          cells: hours.map((h, i) => cellOf(posts, i, h, pastAt[i]!)),
          summary: summaryOf(posts, pastAt),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'sl'));

    const dayPosts = allSectors.flatMap((s) => s.posts);
    const dayHours = hours.map((h, i) => cellOf(dayPosts, i, h, pastAt[i]!));
    let peak: HeatDay['peak'] = null;
    for (const c of dayHours) {
      if (c.open > (peak?.open ?? 0)) peak = { hour: c.hour, open: c.open };
      maxOpen = Math.max(maxOpen, c.open);
    }

    const sectors = opts.hideQuiet
      ? allSectors.filter((s) => s.summary.short > 0 || s.summary.pending > 0)
      : allSectors;
    const empty: HeatDay['empty'] = !withWindows.length
      ? 'no-windows'
      : !visible.length
        ? 'no-urgent'
        : !sectors.length
          ? 'all-quiet'
          : null;

    return {
      dayId: d.id,
      label: d.label,
      date: d.date,
      index,
      phase,
      hours: dayHours,
      peak,
      summary: summaryOf(dayPosts, pastAt),
      allSectors,
      sectors,
      empty,
    };
  });

  const hotspot = (key: 'short' | 'pending'): Hotspot | null => {
    let best: Hotspot | null = null;
    for (const d of days) {
      for (const c of d.hours) {
        if (c.past || c[key] <= (best?.count ?? 0)) continue;
        best = { dayId: d.dayId, label: d.label, hour: c.hour, count: c[key] };
      }
    }
    return best;
  };

  return {
    hours,
    days,
    hotspots: {
      short: opts.short ? hotspot('short') : null,
      pending: opts.pending ? hotspot('pending') : null,
    },
    maxOpen,
  };
}

/**
 * An hour of a day band, or of one of its sectors, looked up by the hour itself.
 * A popover and a tooltip hold on to an hour, never a column index: the axis is
 * rebuilt from every window each time /overview refetches, so an index can run
 * past the end or point at a neighbouring hour. `null` when it is no longer there.
 */
export function locateHour(
  model: HeatModel,
  dayId: string,
  hour: number,
  sectorKey?: string,
): { day: HeatDay; sector: HeatSector | null; index: number; cell: HeatCell } | null {
  const index = model.hours.indexOf(hour);
  const day = model.days.find((d) => d.dayId === dayId);
  if (index < 0 || !day) return null;
  if (sectorKey === undefined) return { day, sector: null, index, cell: day.hours[index]! };
  const sector = day.allSectors.find((s) => s.key === sectorKey);
  return sector ? { day, sector, index, cell: sector.cells[index]! } : null;
}

/** A post strip's span, looked up by where it starts; `null` once it is gone. */
export function locateSpan(
  model: HeatModel,
  dayId: string,
  sectorKey: string,
  postId: string,
  fromMin: number,
): { day: HeatDay; sector: HeatSector; post: HeatPost; span: HeatSpan } | null {
  const day = model.days.find((d) => d.dayId === dayId);
  const sector = day?.allSectors.find((s) => s.key === sectorKey);
  const post = sector?.posts.find((p) => p.postId === postId);
  const span = post?.coverage.find((s) => s.fromMin === fromMin);
  return day && sector && post && span ? { day, sector, post, span } : null;
}

/**
 * The sectors a day-bar column's popover offers to open: those the band draws
 * and that have a post open in the hour. A sector "Skrij sektorje brez težav"
 * hides would open into nothing, so it is only counted.
 */
export function barSectors(day: HeatDay, index: number): { sectors: HeatSector[]; hidden: number } {
  const open = day.allSectors.filter((s) => s.cells[index]!.open > 0);
  const sectors = open.filter((s) => day.sectors.includes(s));
  return { sectors, hidden: open.length - sectors.length };
}

/** Which day bands start open: every day not yet over. Seeded once per session. */
export function defaultDays(payload: OverviewPayload, clock: WallClock): Record<string, boolean> {
  return Object.fromEntries(
    payload.days.map((d) => [d.id, dayPhase(d.date, clock.isoDate) !== 'past']),
  );
}

export interface HeatRow {
  key: string;
  postId: string;
  code: string;
  micro: string;
  urgent: boolean;
  state: 'gap' | 'rejected' | 'pending';
  /** The whole gap, or the whole of that person's cover on the post. */
  fromMin: number;
  toMin: number;
  shiftId: string | null;
  volunteerId: string | null;
  name: string | null;
}

/**
 * What needs doing on a post between two minutes, read from its coverage — a
 * rejection another person stands over is covered, and not listed. A shift that
 * runs across two windows is one row.
 */
export function rowsAt(post: HeatPost, fromMin: number, toMin: number, lenses: HeatLenses): HeatRow[] {
  const touching = post.coverage.filter(
    (s) =>
      s.fromMin < toMin &&
      s.toMin > fromMin &&
      (s.state === 'gap' || s.state === 'rejected'
        ? lenses.short
        : s.state === 'pending'
          ? lenses.pending
          : false),
  );
  const rows = new Map<string, HeatRow>();
  for (const s of touching) {
    const state = s.state as HeatRow['state'];
    const key = s.shiftId ? `${post.postId}:${s.shiftId}` : `${post.postId}:gap:${s.fromMin}`;
    if (rows.has(key)) continue;
    const own = s.shiftId ? post.coverage.filter((x) => x.shiftId === s.shiftId) : [s];
    rows.set(key, {
      key,
      postId: post.postId,
      code: post.code,
      micro: post.micro,
      urgent: post.urgent,
      state,
      fromMin: Math.min(...own.map((x) => x.fromMin)),
      toMin: Math.max(...own.map((x) => x.toMin)),
      shiftId: s.shiftId,
      volunteerId: s.volunteerId,
      name: s.name,
    });
  }
  return [...rows.values()];
}

/**
 * A sector's rows in one hour, split the way the popover groups them, and how
 * many of its posts are settled then (a problem the lenses hide is neither).
 */
export function cellRows(
  sector: HeatSector,
  hourIndex: number,
  hour: number,
  lenses: HeatLenses,
): { short: HeatRow[]; pending: HeatRow[]; settled: number } {
  const short: HeatRow[] = [];
  const pending: HeatRow[] = [];
  let settled = 0;
  for (const p of sector.posts) {
    const m = p.marks[hourIndex];
    if (m === 'ok') settled++;
    if (m !== 'short' && m !== 'pending') continue;
    for (const r of rowsAt(p, hour * 60, hour * 60 + 60, lenses)) {
      (r.state === 'pending' ? pending : short).push(r);
    }
  }
  return { short, pending, settled };
}
